import React, { useState, useRef, useEffect } from "react";
import { url } from "../lib/api";
import { motion, AnimatePresence } from "framer-motion";

const getBotWelcome = (name) => (
  <span>
    <span className="font-bold text-green-600"> 👨‍⚕️ Nutritionist:</span> <br />
    <span className="text-gray-700">
      Welcome{name ? `, ${name}` : ""}! 🌱 I’m here to craft a diet plan just
      for you. Let’s make your nutrition journey inspiring and delicious! 🥗✨
    </span>
  </span>
);

const ChatUI = ({ token, profile }) => {
  const [messages, setMessages] = useState([
    {
      sender: "bot",
      text: getBotWelcome(profile?.name),
    },
  ]);

  // Update welcome message if profile.name changes (e.g., after async load)
  useEffect(() => {
    setMessages((msgs) => {
      if (msgs.length > 0 && msgs[0].sender === "bot") {
        return [
          { ...msgs[0], text: getBotWelcome(profile?.name) },
          ...msgs.slice(1),
        ];
      }
      return msgs;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.name]);
  const [input, setInput] = useState("");
  const chatWindowRef = useRef(null);

  useEffect(() => {
    if (chatWindowRef.current) {
      chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    const userMsg = { sender: "user", text: input };
    setMessages((msgs) => [...msgs, userMsg]);
    setInput("");
    try {
      const res = await fetch(url("/v1/chat/"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message: input }),
      });
      const data = await res.json();
      // Animated bot reply with emoji and highlights
      // Remove repeated profile summary sentence from bot reply
      let reply = data.reply || "";
      // Regex to match the profile summary sentence (case-insensitive, flexible)
      reply = reply
        .replace(
          /Okay [^,]+, based on your profile and previous messages, I understand [^.]+\./i,
          ""
        )
        .trim();
      setMessages((msgs) => [
        ...msgs,
        {
          sender: "bot",
          text: (
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="font-bold text-green-600">👩‍⚕️ Nutritionist:</span>{" "}
              <br />
              <span className="text-gray-700">
                {reply ? (
                  <span>
                    {reply.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
                      part.startsWith("**") && part.endsWith("**") ? (
                        <span key={i} className="font-bold text-blue-600">
                          {part.slice(2, -2)}
                        </span>
                      ) : (
                        part
                      )
                    )}
                  </span>
                ) : (
                  "Sorry, I didn't get that."
                )}
              </span>
            </motion.span>
          ),
        },
      ]);
    } catch (err) {
      setMessages((msgs) => [
        ...msgs,
        {
          sender: "bot",
          text: (
            <span>
              <span className="font-bold text-red-600">⚠️ Error:</span> <br />
              <span className="text-gray-700">
                Unable to connect to the nutrition server. Please try again
                later.
              </span>
            </span>
          ),
        },
      ]);
    }
  };

  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryData, setSummaryData] = useState(null);

  const handleSummary = async () => {
    try {
      const res = await fetch(url("/v1/analyze/summary"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      setSummaryData(data);
      setSummaryOpen(true);
    } catch (err) {
      setSummaryData({ error: "Unable to fetch summary" });
      setSummaryOpen(true);
    }
  };

  return (
    <div className="flex flex-col h-full w-full bg-gradient-to-br from-green-100 via-blue-50 to-blue-200 rounded-3xl shadow-2xl p-4 sm:p-8 border-2 border-blue-200">
      <div
        className="flex-1 mb-4 max-h-[60vh] sm:max-h-[400px] overflow-y-auto"
        id="chat-window"
        ref={chatWindowRef}
      >
        <AnimatePresence>
          {messages.map((msg, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className={`flex mb-2 items-end ${
                msg.sender === "user" ? "justify-end" : "justify-start"
              }`}
            >
              {msg.sender === "bot" && (
                <span className="mr-2 text-2xl sm:text-3xl">👨‍⚕️</span>
              )}
              <div
                className={`px-4 py-3 rounded-2xl max-w-[80%] sm:max-w-md whitespace-pre-line break-words shadow-xl text-sm sm:text-base font-medium transition-all duration-300 ${
                  msg.sender === "user"
                    ? "bg-gradient-to-r from-blue-400 to-blue-600 text-white rounded-br-none border-2 border-blue-300"
                    : "bg-gradient-to-r from-green-100 to-blue-100 text-gray-900 rounded-bl-none border-2 border-green-200"
                }`}
              >
                {msg.text}
              </div>
              {msg.sender === "user" && (
                <span className="ml-2 text-2xl sm:text-3xl">🧑</span>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
      <form
        className="flex flex-col sm:flex-row items-center gap-3 mt-2"
        onSubmit={handleSend}
      >
        <input
          type="text"
          className="w-full sm:flex-1 border-2 border-blue-400 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-300 text-base sm:text-lg bg-white shadow"
          placeholder="Type your message..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
        />
        <div className="flex w-full sm:w-auto gap-2 items-center">
          <button
            type="submit"
            className="flex-1 sm:flex-none bg-gradient-to-r from-green-400 to-blue-500 text-white px-6 py-3 rounded-2xl font-bold shadow-lg hover:scale-105 transition-transform text-base"
          >
            <span role="img" aria-label="send">
              📤
            </span>{" "}
            Send
          </button>
          <button
            type="button"
            onClick={handleSummary}
            className="ml-0 sm:ml-2 bg-white border-2 border-blue-400 text-blue-600 px-4 py-2 rounded-2xl font-semibold hover:bg-blue-50 text-sm"
          >
            Summary
          </button>
        </div>
      </form>

      {summaryOpen && (
        <div className="mt-4 p-4 bg-white rounded-lg shadow">
          <div className="flex justify-between items-start">
            <h3 className="text-lg font-bold">Summary Analysis</h3>
            <button
              onClick={() => setSummaryOpen(false)}
              className="text-sm text-gray-500"
            >
              Close
            </button>
          </div>
          {summaryData ? (
            summaryData.error ? (
              <div className="text-red-500 mt-2">{summaryData.error}</div>
            ) : (
              <div className="mt-2">
                <pre className="whitespace-pre-wrap">{summaryData.summary}</pre>
                <h4 className="mt-2 font-semibold">Suggestions</h4>
                <ul className="list-disc list-inside">
                  {(summaryData.suggestions || []).map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </div>
            )
          ) : (
            <div className="text-gray-500 mt-2">Loading...</div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatUI;
